import { Box, makeStyles } from '@material-ui/core';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  useStarredEntities,
  catalogApiRef,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';
import { Link as RouterLink } from 'react-router-dom';
import StarIcon from '@material-ui/icons/Star';

const useStyles = makeStyles(theme => ({
  favoritesList: {
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1.5),
    },
  },
  favoriteItem: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    display: 'block',
    textDecoration: 'none',
  },
  favoriteItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  favoriteItemTitle: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  favoriteItemBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '999px',
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  noFavorites: {
    padding: theme.spacing(4, 0),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  starIcon: {
    color: '#f3ba37',
  },
}));

const CardTitle = ({ classes }: { classes: ReturnType<typeof useStyles> }) => (
  <Box className={classes.cardTitle}>
    <StarIcon className={classes.starIcon} />
    Favorites
  </Box>
);

// Inner component renders inside InfoCard's built-in ErrorBoundary.
// If useStarredEntities() or useApi(catalogApiRef) throws (e.g. API
// unavailable in the compatWrapper context), InfoCard catches the
// error and displays it instead of crashing the entire home page.
const StarredTemplatesContent = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { starredEntities } = useStarredEntities();

  const {
    value: templates,
    loading,
    error,
  } = useAsync(async () => {
    if (!starredEntities.size) {
      return [];
    }

    const entities = await catalogApi.getEntitiesByRefs({
      entityRefs: [...starredEntities],
      fields: [
        'kind',
        'metadata.namespace',
        'metadata.name',
        'metadata.title',
        'metadata.description',
      ],
    });

    return entities.items.filter(
      (e): e is Entity => !!e && e.kind === 'Template',
    );
  }, [catalogApi, starredEntities]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (!templates || templates.length === 0) {
    return (
      <div className={classes.noFavorites}>
        You haven't starred any projects yet
      </div>
    );
  }

  return (
    <div className={classes.favoritesList}>
      {templates
        .sort((a, b) =>
          (a.metadata.title ?? a.metadata.name).localeCompare(
            b.metadata.title ?? b.metadata.name,
          ),
        )
        .map(template => {
          const params = entityRouteParams(template);
          return (
            <Link
              key={stringifyEntityRef(template)}
              component={RouterLink}
              to={`/catalog/${params.namespace}/${params.kind}/${params.name}`}
              className={classes.favoriteItem}
            >
              <div className={classes.favoriteItemContent}>
                <div>
                  <div className={classes.favoriteItemTitle}>
                    {template.metadata.title || template.metadata.name}
                  </div>
                  <span className={classes.favoriteItemBadge}>Template</span>
                </div>
              </div>
            </Link>
          );
        })}
    </div>
  );
};

export const StarredTemplatesCard = () => {
  const classes = useStyles();

  return (
    <InfoCard title={<CardTitle classes={classes} />}>
      <StarredTemplatesContent />
    </InfoCard>
  );
};
